<template>
  <div class="get-quotes-page">
    <!-- Header Section -->
    <AppHeader 
      header-text="Drivers in your state can save up to $600/year"
      button-text="FREE CALL"
    />

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="navigation">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Previous Question
        </button>
      </div>
    </div>

    <!-- Get Quotes Section -->
    <section class="get-quotes-section">
      <div class="container">
        <div class="quotes-content">
          <PageTitle title="Last Step, Get Your Quotes" />
          
          <div class="quotes-form">
            <!-- Phone Number Input -->
            <div class="input-group">
              <input
                type="tel"
                v-model="phoneNumber"
                placeholder="Phone Number"
                class="phone-input"
                :class="{ 'error': showPhoneError }"
                @input="clearPhoneError"
                maxlength="14"
              />
              <div v-if="showPhoneError" class="error-message">{{ phoneErrorMessage }}</div>
            </div>

            <!-- Get My Quote Button -->
            <div class="quote-button-section">
              <button 
                class="get-quote-btn"
                :class="{ 'submitting': isSubmitting }"
                :disabled="isSubmitting"
                @click="getMyQuote"
              >
                <span v-if="isSubmitting" class="loading-spinner"></span>
                {{ isSubmitting ? 'SUBMITTING...' : 'GET MY QUOTE' }}
              </button>
            </div>

            <!-- Legal Terms Text -->
            <div class="legal-terms">
              <p class="terms-text">
                By clicking Get My Quote and submitting this form, I provide express written consent to the SMS terms 
                below and to receive pre-recorded/automated/artificial voice calls, alerts, and texts from policyvista.com 
                and it's marketing partners. Message and data rates may apply. I understand I may opt-out of phone and 
                SMS communication by replying 'STOP' or 'HELP' for more info, or by contacting contact@policyvista.com. 
                I understand I will receive marketing communications to the phone number provided, which includes 
                wireless numbers, and if applicable, numbers previously registered on the Federal and State DNC registries. 
                See <a href="#" class="terms-link">SMS Policy</a> for more details. I certify that I am a US resident over 18, and all information submitted 
                with this request is true and accurate to the best of my knowledge. Consent is not a condition to receive 
                services from policyvista.com and if I do not consent, I can call (888) 845-9747 to speak to a professional 
                to obtain an insurance quote. I acknowledge my consent can be revoked at any time.
              </p>
              
              <p class="consent-text">
                By clicking 'Get My Quote', you consent to PolicyVista saving the information you entered and sharing it 
                with insurance carriers so you can get the most up-to-date quotes, no matter what device you're using. 
                You also agree to PolicyVista's <a href="#" class="terms-link">Privacy Policy</a> and <a href="#" class="terms-link">Terms of Service</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Links -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { submitDataAndLog } from '@/utils/localStorage'
import PageTitle from '@/components/PageTitle.vue'

export default {
  name: 'GetQuotesPage',
  components: {
    AppHeader,
    AppFooter,
    PageTitle
  },
  data() {
    return {
      userQuoteData: null,
      phoneNumber: '',
      showPhoneError: false,
      phoneErrorMessage: 'Required',
      isSubmitting: false
    }
  },
  mounted() {
    this.loadUserData()
  },
  methods: {
    loadUserData() {
      const storedData = localStorage.getItem('quoteData')
      if (storedData) {
        try {
          this.userQuoteData = JSON.parse(storedData)
          // 检查是否有邮箱信息
          if (!this.userQuoteData.email) {
            // 如果没有邮箱信息，跳转回email-address页面
            this.$router.push('/email-address')
            return
          }
          // 加载之前输入的电话号码（如果有的话）
          if (this.userQuoteData.phone) {
            this.phoneNumber = this.userQuoteData.phone
          }
        } catch (error) {
          console.error('Error parsing stored data:', error)
          this.$router.push('/')
        }
      } else {
        // 如果没有数据，跳转回首页
        this.$router.push('/')
      }
    },
    
    saveUserData() {
      localStorage.setItem('quoteData', JSON.stringify(this.userQuoteData))
    },
    
    clearPhoneError() {
      if (this.phoneNumber && this.phoneNumber.trim().length > 0) {
        this.showPhoneError = false
      }
    },
    
    formatPhoneNumber(phone) {
      // 移除所有非数字字符
      const cleaned = phone.replace(/\D/g, '')
      
      // 格式化为 (xxx) xxx-xxxx
      if (cleaned.length >= 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
      } else if (cleaned.length >= 6) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
      } else if (cleaned.length >= 3) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
      }
      return cleaned
    },
    
    isValidPhoneNumber(phone) {
      const cleaned = phone.replace(/\D/g, '')
      return cleaned.length === 10
    },
    
    async getMyQuote() {
      console.log('GET MY QUOTE button clicked')
      
      // 防止重复提交
      if (this.isSubmitting) {
        return
      }
      
      // 验证电话号码
      if (!this.phoneNumber || this.phoneNumber.trim().length === 0) {
        this.showPhoneError = true
        this.phoneErrorMessage = 'Required'
        return
      }
      
      if (!this.isValidPhoneNumber(this.phoneNumber)) {
        this.showPhoneError = true
        this.phoneErrorMessage = 'Please enter a valid 10-digit phone number'
        return
      }
      
      this.showPhoneError = false
      
      // 格式化并保存电话号码
      const formattedPhone = this.formatPhoneNumber(this.phoneNumber)
      this.userQuoteData.phone = formattedPhone
      this.saveUserData()
      
      console.log('Phone number saved:', formattedPhone)
      console.log('Final user data:', this.userQuoteData)
      
      // 开始提交
      this.isSubmitting = true
      
      try {
        // 提交数据并记录日志（使用complete=1参数，等待完成）
        await submitDataAndLog('getquotes', true, true)
        
        // 跳转到Accepted页面
        this.$router.push('/accepted')
      } catch (error) {
        console.error('Error during final submission:', error)
        
        // 即使出错也跳转到Accepted页面
        this.$router.push('/accepted')
      } finally {
        this.isSubmitting = false
      }
    },
    
    goBack() {
      this.$router.push('/email-address')
    }
  },
  
  watch: {
    phoneNumber(newValue) {
      // 实时格式化电话号码
      const formatted = this.formatPhoneNumber(newValue)
      if (formatted !== newValue) {
        this.phoneNumber = formatted
      }
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.get-quotes-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Navigation */
.navigation {
  padding: 1rem 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #999;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: #666;
}

/* Get Quotes Section */
.get-quotes-section {
  padding: 2rem 0 4rem;
  background: #fff;
  flex: 1;
}

.quotes-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.quotes-form {
  margin-top: 2rem;
  text-align: left;
}

.input-group {
  position: relative;
  margin-bottom: 2rem;
  text-align: center;
}

.phone-input {
  width: 100%;
  max-width: 400px;
  padding: 1rem 0;
  border: none;
  border-bottom: 2px solid #e9ecef;
  border-radius: 0;
  background: transparent;
  font-size: 1rem;
  color: #333;
  transition: border-color 0.3s ease;
  outline: none;
  text-align: center;
}

.phone-input:focus {
  border-bottom-color: #007bff;
}

.phone-input.error {
  border-bottom-color: #dc3545;
}

.phone-input::placeholder {
  color: #999;
  font-weight: 400;
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  text-align: center;
}

/* Quote Button Section */
.quote-button-section {
  margin: 3rem 0;
  text-align: center;
}

.get-quote-btn {
  padding: 1.2rem 4rem;
  border: none;
  border-radius: 8px;
  background: #007bff;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 300px;
  text-transform: uppercase;
}

.get-quote-btn:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.3);
}

.get-quote-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.get-quote-btn.submitting {
  background: #6c757d;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Legal Terms */
.legal-terms {
  margin-top: 2rem;
  text-align: left;
}

.terms-text, .consent-text {
  font-size: 0.8rem;
  color: #666;
  line-height: 1.4;
  margin-bottom: 1rem;
}

.terms-link {
  color: #007bff;
  text-decoration: underline;
}

.terms-link:hover {
  color: #0056b3;
}

/* Responsive Design */
@media (max-width: 768px) {
  .quotes-content {
    padding: 0 1rem;
  }

  .phone-input {
    max-width: 100%;
  }

  .get-quote-btn {
    width: 100%;
    max-width: 350px;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    min-width: unset;
  }

  .terms-text, .consent-text {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .quotes-content {
    padding: 0 0.5rem;
  }

  .phone-input {
    padding: 0.8rem 0;
    font-size: 0.9rem;
  }

  .get-quote-btn {
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
  }

  .terms-text, .consent-text {
    font-size: 0.7rem;
    line-height: 1.3;
  }
}
</style>
